import {
  Col,
  Row,
  Select,
  Input,
  DatePicker,
  Form,
  Button,
  Space,
  Divider
} from "antd";
import Title from "antd/lib/typography/Title";
import React from "react";
import FormSection from "../../../core/ui/components/FormSection";
import Page, { PageProps } from "../../../core/ui/pages/Page";
import { ISearchCriteriaPresenter } from "../../presenter/SearchCriteriaPresenter";
import CollectionCheckboxGroup from "../components/criteria/CollectionCheckboxGroup";
import DateSelectionInput from "../components/criteria/DateSelectionInput";
import IndexTreeSelectInput from "../components/criteria/IndexTreeSelectInput";
import OrganisationCheckboxGroup from "../components/criteria/OrganisationCheckboxGroup";
import SearchTermInput from "../components/criteria/SearchTermInput";
const { Option } = Select;
const { RangePicker } = DatePicker;

export interface Props extends PageProps {}

export interface State extends ISearchCriteriaPresenter {
  allPanelsCollapsed: boolean;
}

export default class AdvancedSearchCriteriaPage extends React.Component<
  Props,
  State
> {
  private subscriptionId: number = 0;

  constructor(props: any) {
    super(props);
    this.state = { ...props, allPanelsCollapsed: false };
  }

  componentDidMount() {
    this.subscriptionId = this.state.store.subscribe((result) => {
      return this.setState((prevState) => result);
    });
  }

  componentWillUnmount() {
    this.state.store.unsubscribe(this.subscriptionId);
  }

  render() {
    const { translate } = this.props;
    const {
      organisations,
      courtCaseTypes,
      decisionTypes,
      onUpdateCriteria,
      indexTrees,
      loaders,
      allPanelsCollapsed,
      indexCategories,
      searchQuery,
      searchText,
      onSearch
    } = this.state;
    const indexLoading = loaders.criteria;

    let selectedOrganisations: string[] | undefined =
      searchQuery?.organisation_id?.split(",");

    return (
      <Page
        {...this.props}
        size={18}
        offset={3}
        className="advanced-search-criteria"
      >
        <Row className={"content-wrapper advanced-search-criteria-container"}>
          <Col xs={24} sm={24}>
            <Title level={1} style={{ marginBottom: 30 }}>
              {translate("search.bar.advanced_search_action")}
            </Title>
          </Col>
          <Col xs={24} sm={24}>
            <Form name="advanced-search" layout={"vertical"}>
              <Row gutter={[0, 12]}>
                <Col xs={24} sm={24} className="align-right">
                  <Space size="small">
                    <Button
                      onClick={() => {
                        window.location.reload();
                      }}
                      icon={<i className="fas fa-search-minus"></i>}
                    >
                      {translate("search.advanced.cancel")}
                    </Button>
                    <Button
                      onClick={onSearch}
                      type="primary"
                      icon={<i className="fas fa-search-plus"></i>}
                    >
                      {translate("search.advanced.do_search")}
                    </Button>
                  </Space>
                </Col>
                <Col xs={24} sm={24}>
                  <CollectionCheckboxGroup
                    translate={translate}
                    onSelectionChanged={(selectedItems) => {
                      onUpdateCriteria({
                        collection: selectedItems?.join(",")
                      });
                    }}
                  />
                </Col>
                <Col xs={24} sm={24}>
                  <Divider />
                </Col>
                <Col xs={24} sm={24} className="align-right">
                  <Space size="small">
                    <Button
                      size="small"
                      type="link"
                      icon={<i className="fas fa-chevron-down"></i>}
                      onClick={() =>
                        this.setState({
                          allPanelsCollapsed: false
                        })
                      }
                    >
                      {translate("search.advanced.show_all")}
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      icon={<i className="fas fa-chevron-up"></i>}
                      onClick={() =>
                        this.setState({
                          allPanelsCollapsed: true
                        })
                      }
                    >
                      {translate("search.advanced.hide_all")}
                    </Button>
                  </Space>
                </Col>

                <Col xs={24} sm={24}>
                  <FormSection
                    compact
                    collapsible
                    collapsed={allPanelsCollapsed}
                    title={translate("search.advanced.term_criteria")}
                    subtitle={translate("search.advanced.term_subtitle")}
                  >
                    <SearchTermInput
                      value={searchText || ""}
                      translate={translate}
                      onChange={(
                        allFields: string[],
                        selectedFields: string[],
                        searchTerm
                      ) => {
                        let criteria: any = allFields?.reduce(
                          (prev, current) => {
                            prev[current] = selectedFields?.some(
                              (item) => item === current
                            )
                              ? searchTerm
                              : undefined;
                            return prev;
                          },
                          {}
                        );
                        criteria.searchText = searchTerm;
                        onUpdateCriteria(criteria);
                      }}
                    />
                  </FormSection>
                </Col>

                <Col xs={24} sm={24}>
                  <FormSection
                    compact
                    collapsible
                    collapsed={allPanelsCollapsed}
                    title={translate("search.advanced.organisation")}
                    subtitle={""}
                  >
                    <OrganisationCheckboxGroup
                      selectedOrganisations={selectedOrganisations}
                      organisations={organisations}
                      translate={translate}
                      horizontal
                      onChange={(list) => {
                        onUpdateCriteria?.({
                          organisation_id: list?.join(",")
                        });
                      }}
                    />
                  </FormSection>
                </Col>

                <Col xs={24} sm={24}>
                  <FormSection
                    compact
                    collapsible
                    collapsed={allPanelsCollapsed}
                    title={translate("search.advanced.court_case_criteria")}
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} sm={24} md={24} xl={24} xxl={8}>
                        <Form.Item
                          name="courtCaseTypes"
                          label={translate("search.advanced.court_case_type")}
                          className="mb-0"
                        >
                          <Select
                            mode="multiple"
                            allowClear
                            placeholder={translate(
                              "search.advanced.court_case_type_placeholder"
                            )}
                            className={"png-select"}
                            onChange={(value) => {
                              onUpdateCriteria({
                                court_case_type_ss: value?.toString()
                              });
                            }}
                          >
                            {courtCaseTypes?.map((item, index) => (
                              <Option
                                key={`court-case-type-${index}`}
                                value={item.code}
                              >
                                {item.value}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} xl={12} xxl={8}>
                        <Form.Item
                          name="courtCaseNumber"
                          label={translate("search.advanced.court_case_number")}
                          className="mb-0"
                        >
                          <Input
                            placeholder={translate(
                              "search.advanced.court_case_number_placeholder"
                            )}
                            onChange={(event) => {
                              onUpdateCriteria({
                                court_case_number_ss:
                                  event?.target.value.toString()
                              });
                            }}
                          ></Input>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} xl={12} xxl={8}>
                        <Form.Item
                          name="courtCaseDescription"
                          label={translate(
                            "search.advanced.court_case_description"
                          )}
                          className="mb-0"
                        >
                          <Input
                            placeholder={translate(
                              "search.advanced.court_case_description_placeholder"
                            )}
                            onChange={(event) => {
                              onUpdateCriteria({
                                court_case_description_ss:
                                  event?.target.value.toString()
                              });
                            }}
                          ></Input>
                        </Form.Item>
                      </Col>
                    </Row>
                  </FormSection>
                </Col>

                <Col xs={24} sm={24}>
                  <FormSection
                    compact
                    collapsible
                    collapsed={allPanelsCollapsed}
                    title={translate("search.advanced.case_decision_criteria")}
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} sm={24} md={12} xl={12} xxl={8}>
                        <Form.Item
                          name="decisionTypes"
                          label={translate(
                            "search.advanced.case_decision_type"
                          )}
                          className="mb-0"
                        >
                          <Select
                            mode="multiple"
                            allowClear
                            placeholder={translate(
                              "search.advanced.case_decision_type_placeholder"
                            )}
                            className={"png-select"}
                            onChange={(value) => {
                              onUpdateCriteria({
                                case_decision_type_ss: value?.toString()
                              });
                            }}
                            // onChange={handleChange}
                          >
                            {decisionTypes?.map((item, index) => (
                              <Option
                                key={`decision-type-${index}`}
                                value={item.id}
                              >
                                {item.value}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} xl={12} xxl={8}>
                        <Form.Item
                          name="decisionDescription"
                          label={translate(
                            "search.advanced.case_decision_description"
                          )}
                          className="mb-0"
                        >
                          <Input
                            placeholder={translate(
                              "search.advanced.case_decision_description_placeholder"
                            )}
                            onChange={(event) => {
                              onUpdateCriteria({
                                case_decision_description_ss:
                                  event?.target.value.toString()
                              });
                            }}
                          ></Input>
                        </Form.Item>
                      </Col>
                    </Row>
                  </FormSection>
                </Col>

                <Col xs={24} sm={24}>
                  <FormSection
                    compact
                    collapsible
                    collapsed={allPanelsCollapsed}
                    title={translate("search.advanced.date_criteria")}
                  >
                    <DateSelectionInput
                      translate={translate}
                      onChange={(type, range) => {
                        onUpdateCriteria({
                          [type]: range
                        });
                      }}
                    />
                  </FormSection>
                </Col>

                <Col xs={24} sm={24}>
                  <FormSection
                    compact
                    collapsible
                    collapsed={allPanelsCollapsed}
                    title={translate("search.advanced.index_criteria")}
                  >
                    <IndexTreeSelectInput
                      translate={translate}
                      indexCategories={indexCategories}
                      loading={indexLoading || false}
                      indexTrees={indexTrees || []}
                      onSelect={(list) =>
                        onUpdateCriteria?.({
                          index: list.join(",")
                        })
                      }
                    />
                  </FormSection>
                </Col>

                <Col xs={24} sm={24} className="align-right">
                  <Space size="small">
                    <Button
                      onClick={() => {
                        window.location.reload();
                      }}
                      icon={<i className="fas fa-search-minus"></i>}
                    >
                      {translate("search.advanced.cancel")}
                    </Button>
                    <Button
                      onClick={onSearch}
                      type="primary"
                      icon={<i className="fas fa-search-plus"></i>}
                    >
                      {translate("search.advanced.do_search")}
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Page>
    );
  }
}
